import React from 'react';
import { Box } from '@chakra-ui/react';

// Fondo "interactivo": antes todas las pantallas del escáner (ScannerShell,
// USBScanner, ManualScanner) usaban el mismo tono coral fijo sin importar
// qué se estuviera registrando. Ahora sigue el color del TIPO de lector
// activo — verde para Entrada, naranjo para Salida, azul para Consulta —
// mismos 3 colores de marca que ya define el plugin PHP (Sistema de
// Colores de Marca: verde académico/naranjo destacado/azul educativo) y
// mismas 2 pastillas (MODE_GRADIENT) que ya usa el switch Entrada/Salida y
// el botón de Consulta en ScannerShell.js — así todo el flujo de escaneo
// se lee como un solo sistema de color, no como pantallas sueltas.
export const MODE_TONE = {
  // Entrada — verde académico (#52C41A) + ok.500, el verde de "éxito" que
  // ya usa el resto de la app (ver theme/styles.js).
  access_log: { glow: '#52C41A', glow2: '#1E9E63', washMid: '#E7F7EE', washEnd: '#C7ECD7' },
  // Salida — naranjo destacado (#FA8C16) + warn.500, el ámbar de "atención"
  // que ya usa el resto de la app.
  exit_log:   { glow: '#FA8C16', glow2: '#C77A12', washMid: '#FDF1DD', washEnd: '#F6DBAB' },
  // Consulta — azul educativo (#1890FF) + sky.ink, el azul que ya usa el
  // resto de la app.
  info_only:  { glow: '#1890FF', glow2: '#2A66B4', washMid: '#EAF2FD', washEnd: '#CEE0FA' },
};

// Fallback para pantallas sin `mode` todavía resuelto (ej. loading inicial) —
// Entrada es el modo más común, así que es el default más neutro.
const DEFAULT_TONE = MODE_TONE.access_log;

const hexToRgbTriplet = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
};

// Fondo cálido compartido por TODAS las pantallas del escáner (resultado en
// ScannerShell, y también USBScanner/ManualScanner) — casi blanco, con un
// matiz sutil del color de `mode` (nada de negro) para que se lea bien a
// distancia y las tarjetas translúcidas dejen asomar el degradado detrás.
// Misma receta que el tono coral fijo de antes (2 brillos radiales + 1
// lavado lineal de 3 paradas), solo recoloreada por tipo.
export const getShellBg = (mode) => {
  const t = MODE_TONE[mode] ?? DEFAULT_TONE;
  return `
    radial-gradient(ellipse 65% 50% at 10% 5%, rgba(${hexToRgbTriplet(t.glow)},.20), transparent 62%),
    radial-gradient(ellipse 60% 46% at 95% 92%, rgba(${hexToRgbTriplet(t.glow2)},.16), transparent 68%),
    linear-gradient(160deg, #FFFBF8 0%, ${t.washMid} 55%, ${t.washEnd} 100%)
  `;
};

// Compatibilidad: algunos lugares (loading genérico, pantallas sin `mode`
// todavía) pueden seguir usando un fondo fijo — mismo look que antes tenía
// el tono coral, ahora como el default verde de Entrada.
export const WARM_SHELL_BG = getShellBg();

// Formas cálidas decorativas (blobs difuminados) que se ven detrás del
// contenido sobre getShellBg(mode) — puramente visuales, no interactivas.
// Mismo `mode` que el fondo, para que los blobs no desentonen con el
// lavado de color de atrás.
export const WarmShapes = ({ mode }) => {
  const t = MODE_TONE[mode] ?? DEFAULT_TONE;
  return (
    <Box position="absolute" inset={0} overflow="hidden" pointerEvents="none" zIndex={0}>
      <Box position="absolute" top="-10%" left="-8%" w="50%" h="42%" borderRadius="full"
        bg={t.glow} opacity={0.16} filter="blur(70px)" />
      <Box position="absolute" bottom="-14%" right="-10%" w="55%" h="46%" borderRadius="full"
        bg={t.glow2} opacity={0.14} filter="blur(80px)" />
      <Box position="absolute" top="32%" right="12%" w="24%" h="22%" borderRadius="full"
        bg={t.washEnd} opacity={0.55} filter="blur(50px)" />
    </Box>
  );
};

// Ícono/anillo/acentos puntuales que necesitan el color de `mode` fuera de
// un fondo (ej. el ícono pulsante de USBScanner, el marco de escaneo de
// QRScanner) — evita repetir el lookup de MODE_TONE en cada componente.
export const getModeAccent = (mode) => (MODE_TONE[mode] ?? DEFAULT_TONE).glow;
